// "पुरुष अनुसार प्रत्यय": the 3 × 2 table of endings of one tense (rows उत्तम / मध्यम / अन्य, columns एकवचन / बहुवचन,
// two numbers only). सामान्य: the book's standard ending of each cell with the pronouns in brackets. विकल्प: every ending
// of the cell as the notes write it (मि / आमि / एमि), the standard one filled, with the notes' pronouns (अहं, हं, अम्मि).

import type { FC, ReactNode } from 'react'
import type { Lang, Num, Person } from '@/prakrit/lib/grammar/types'
import type { PratyayaForm, PratyayaTable, StemChange } from '@/prakrit/lib/grammar/pratyaya'
import type { KalMode } from '@/prakrit/components/grammar/kal/kal-tabs'

import { cn } from '@/lib/utils'
import { NUMBERS, PERSONS } from '@/prakrit/lib/grammar/types'
import { NUMBER_LABEL, PERSON_LABEL } from '@/prakrit/lib/grammar/labels'
import { fullEnding } from '@/prakrit/lib/grammar/pratyaya'
import { PANEL } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { NUMBER_EN, PERSON_EN } from '@/prakrit/components/grammar/dhatu/labels'
import { subjectsOf } from '@/prakrit/components/grammar/kal/kal-data'
import { PratyayaChip, distinctEndings } from '@/prakrit/components/grammar/kal/pratyaya-chip'

interface PratyayaTableViewProps {
    root: string
    table: PratyayaTable
    mode: KalMode
    /** "वर्तमान काल" — the caption reads "वर्तमान काल — सामान्य प्रत्यय". */
    tenseHi: string
    tenseEn: string
}

/** What happens to the root before the ending, with this root as the example (हस → हसा). */
const changeNote = (change: StemChange, root: string, f: PratyayaForm): Lang => {
    const ex = `${root} → ${f.stem}`

    if (change === 'aa') return { hi: `उत्तम पुरुष के प्रत्यय से पहले धातु का अ → आ होता है (${ex})।`, en: `Before the speaker’s endings the अ of the root becomes आ (${ex}).` }
    if (change === 'e') return { hi: `धातु का अ → ए होता है (${ex})।`, en: `The अ of the root becomes ए (${ex}).` }
    if (change === 'i') return { hi: `प्रत्यय से पहले धातु का अ → इ होता है (${ex})।`, en: `Before the ending the अ of the root becomes इ (${ex}).` }
    if (change === 'avi') return { hi: `भविष्यत् के प्रत्यय से पहले आव का अ → इ होता है (${ex})।`, en: `Before the future ending the अ of आव becomes इ (${ex}).` }
    if (change === 'short') return { hi: `संयुक्त अक्षर से पहले दीर्घ स्वर ह्रस्व हो जाता है: ${root} + ${f.ending} → ${f.form}।`, en: `A long vowel before a conjunct becomes short: ${root} + ${f.ending} → ${f.form}.` }
    if (change === 'ae') return { hi: `आकारान्त आदि धातु में अ विकरण जोड़कर भी ज्ज, ज्जा लगते हैं; अ → ए: ${root} + अ → ${f.stem}, ${f.form}।`, en: `Roots ending in आ, ओ … also add the vikaraṇa अ before -ज्ज, -ज्जा, and it becomes ए: ${f.form}.` }
    if (change === 'ai') return { hi: `इज्जसु, इज्जहि, इज्जे लगने पर अ + इ = ए: ${f.form}।`, en: `Before -इज्जसु, -इज्जहि, -इज्जे, अ + इ becomes ए: ${f.form}.` }

    return { hi: 'दा धातु के विकल्प रूपों में दा → दे होता है।', en: 'In the variant forms of दा, दा becomes दे.' }
}

const Subjects: FC<{ p: Person, n: Num, mode: KalMode }> = ({ p, n, mode }): ReactNode => {
    const s = subjectsOf(p, n)

    return (
        <p className='text-sm leading-snug text-muted-foreground'>
            <span lang='pra' className='font-prakrit'>({(mode === 'vikalp' ? s.vikalp : s.book).join(', ')})</span>
        </p>
    )
}

/** On the विकल्प tab: one chip per distinct ending as the notes write it; the सामान्य one is filled. */
const VikalpChips: FC<{ forms: PratyayaForm[] }> = ({ forms }): ReactNode => {
    const seen = new Map<string, PratyayaForm>()

    for (const f of forms) {
        const key = fullEnding(f)
        const had = seen.get(key)

        if (!had || (had.status !== 'core' && f.status === 'core')) seen.set(key, f)
    }

    return (
        <span className='flex flex-wrap gap-1.5'>
            {Array.from(seen, ([label, f]) => (
                <span
                    key={label}
                    lang='pra'
                    title={f.status === 'core' ? 'सामान्य · standard' : 'विकल्प · variant'}
                    className={cn(
                        'inline-flex min-h-9 min-w-9 items-center justify-center rounded-xl border px-2.5 font-prakrit text-[0.9375rem] font-semibold leading-none',
                        f.status === 'core' ? 'border-terracotta bg-terracotta text-background shadow-sm' : 'border-terracotta/60 bg-terracotta/8 text-terracotta'
                    )}
                >
                    {label}
                </span>
            ))}
        </span>
    )
}

export const PratyayaTableView: FC<PratyayaTableViewProps> = ({ root, table, mode, tenseHi, tenseEn }): ReactNode => {
    const changes = new Map<StemChange, PratyayaForm>()
    const shownOnVikalp = new Set<StemChange>(['short', 'ae', 'ai', 'de', 'avi'])

    for (const p of PERSONS) {
        for (const n of NUMBERS) {
            const cell = table[p][n]

            for (const f of mode === 'samanya' ? cell.samanya : cell.all) {
                if (!f.change || changes.has(f.change)) continue
                if (mode === 'vikalp' && !shownOnVikalp.has(f.change)) continue

                changes.set(f.change, f)
            }
        }
    }

    const anyVikalp = PERSONS.some(p => NUMBERS.some(n => table[p][n].vikalp.length > 0))
    const caption = mode === 'samanya'
        ? { hi: `${tenseHi} — सामान्य प्रत्यय`, en: `${tenseEn}: standard endings` }
        : { hi: `${tenseHi} — विकल्प प्रत्यय`, en: `${tenseEn}: every ending` }

    return (
        <div className='flex flex-col gap-3'>
            <div className={cn(PANEL, 'overflow-hidden')}>
                <table className='w-full table-fixed border-collapse text-left'>
                    <caption className='border-b border-border px-4 py-3.5 text-left sm:px-6'>
                        <span lang='hi' className='hindi text-base font-semibold'>{caption.hi}</span>
                        <span aria-hidden='true' className='text-muted-foreground'> · </span>
                        <span lang='en' className='text-sm text-muted-foreground'>{caption.en}</span>
                    </caption>
                    <colgroup>
                        <col className='w-[30%] sm:w-1/4' />
                        <col />
                        <col />
                    </colgroup>
                    <thead className='bg-surface-muted/80'>
                        <tr className='border-b border-border'>
                            <th scope='col' className='px-4 py-3 align-bottom font-normal leading-tight sm:px-6'>
                                <span lang='hi' className='hindi block text-sm font-semibold text-muted-foreground'>पुरुष</span>
                                <span lang='en' className='block text-xs text-muted-foreground'>person</span>
                            </th>
                            {NUMBERS.map(n => (
                                <th key={n} scope='col' className='px-2 py-3 align-bottom font-normal leading-tight sm:px-4'>
                                    <span lang='hi' className='hindi block text-sm font-semibold text-muted-foreground'>{NUMBER_LABEL[n].hi}</span>
                                    <span lang='en' className='block text-xs text-muted-foreground'>{NUMBER_EN[n]}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {PERSONS.map(p => (
                            <tr key={p} className='border-b border-border last:border-b-0'>
                                <th scope='row' className='px-4 py-4 align-middle font-normal leading-tight sm:px-6'>
                                    <span lang='hi' className='hindi block text-[0.9375rem] font-semibold text-foreground'>{PERSON_LABEL[p].hi}</span>
                                    <span lang='en' className='block text-xs text-muted-foreground'>{PERSON_EN[p]}</span>
                                </th>
                                {NUMBERS.map(n => {
                                    const cell = table[p][n]

                                    return (
                                        <td key={n} className='px-2 py-4 align-top sm:px-4'>
                                            <div className='flex min-w-0 flex-col gap-2'>
                                                <Subjects p={p} n={n} mode={mode} />
                                                {mode === 'samanya' ? (
                                                    <span className='flex flex-wrap gap-2'>
                                                        {distinctEndings(cell.samanya).map(f => (
                                                            <PratyayaChip key={`${f.marker ?? ''}|${f.ending}`} form={f} tone='samanya' />
                                                        ))}
                                                    </span>
                                                ) : (
                                                    <VikalpChips forms={cell.all} />
                                                )}
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ul className='flex flex-col gap-1.5 text-sm text-muted-foreground'>
                {[...changes].map(([c, f]) => {
                    const note = changeNote(c, root, f)

                    return (
                        <li key={c}>
                            <span lang='hi' data-ex='hi' className='hindi'>{note.hi}</span>
                            <span aria-hidden='true' className='ex-sep'> · </span>
                            <span lang='en' data-ex='en'>{note.en}</span>
                        </li>
                    )
                })}
                {mode === 'vikalp' && (
                    <li>
                        <span lang='hi' data-ex='hi' className='hindi'>
                            {anyVikalp
                                ? 'भरा हुआ खाना सामान्य प्रत्यय है, बाकी विकल्प। विकल्प भी सही हैं, पर अभ्यास में सामान्य प्रत्यय पूछा जाता है (अभ्यास में "विकल्प सहित" चुनें तो विकल्प भी)।'
                                : 'इस काल में कोई विकल्प प्रत्यय नहीं; सभी रूप सामान्य हैं।'}
                        </span>
                        <span aria-hidden='true' className='ex-sep'> · </span>
                        <span lang='en' data-ex='en'>
                            {anyVikalp
                                ? 'The filled chip is the standard ending, the rest are variants. Variants are right too; practice asks for the standard one unless you choose "Include variants".'
                                : 'This tense has no variant endings; every form is standard.'}
                        </span>
                    </li>
                )}
            </ul>
        </div>
    )
}
